/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { type ReactNode } from 'react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/shared/ui/components/shadcn';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { cn } from '@/shared/ui/utils/tailwind';
import { preventOpenComboboxEscape, preventSelectPortalDismiss } from './select-portal-dismiss';

/** Visual density for the adaptive form shell panel. */
export type AdaptiveFormShellDensity = 'default' | 'compact' | 'settings';

export interface AdaptiveFormShellProps {
  /** Whether the shell is visible. */
  open: boolean;
  /** Invoked when the user dismisses the shell (overlay, close button, escape). */
  onClose: () => void;
  /** Heading shown at the top of the shell. */
  title: string;
  /** Scrollable body content. */
  children: ReactNode;
  /** Pinned footer, typically the submit action. */
  footer?: ReactNode;
  /**
   * Panel density. `default` keeps the wizard-wide modal; `compact` narrows
   * to ~640px with denser padding for short create forms; `settings` is the
   * fixed stage used by the calendar settings, where a navigation column
   * holds its place while only the panel beside it scrolls.
   */
  density?: AdaptiveFormShellDensity;
}

/*
 * The phone's form rises from the bottom edge, not in from the right.
 *
 * A panel sliding in from the right is a desktop drawer: it reads as a
 * second column that happened to overlap the first. On a phone there is no
 * first column to sit beside — the form is the screen — and the edge the
 * hand is already on is the bottom one. Stopping short of the top leaves a
 * strip of the screen it came from visible, which is what says "a decision
 * over this" rather than "you are somewhere else now".
 */
const mobilePanelClasses = cn(
  'flex flex-col gap-0 p-0',
  'w-full max-w-[100vw] border-(--border-subtle) bg-(--surface-primary)',
);

type ShellChrome = {
  desktopPanel: string;
  header: string;
  title: string;
  body: string;
  footer: string;
};

const defaultChrome: ShellChrome = {
  desktopPanel: cn(
    'flex flex-col gap-0 p-0',
    'w-[min(1040px,96vw)] max-w-none max-h-[80vh]',
    'rounded-(--radius-lg) border-(--border-subtle) bg-(--surface-primary)',
  ),
  header: cn(
    'flex shrink-0 items-center justify-between',
    'px-(--space-6) py-(--space-5) border-b border-(--border-subtle)',
  ),
  // Page-scale title for wide shells (tok-061 --text-app-title)
  title: cn(
    'm-0 text-(--text-primary)',
    'font-[family-name:var(--font-family)]',
    'font-[weight:var(--font-semibold)]',
    'text-[length:var(--text-app-title)]',
    'leading-[var(--leading-tight)]',
    'tracking-[var(--tracking-app-title)]',
  ),
  body: cn('min-h-0 flex-1 overflow-y-auto p-(--space-6)'),
  footer: cn(
    'shrink-0 border-t border-(--border-subtle) px-(--space-6) py-(--space-4)',
    'flex justify-end',
  ),
};

const compactChrome: ShellChrome = {
  desktopPanel: cn(
    'flex flex-col gap-0 p-0',
    'w-[min(640px,96vw)] max-w-none max-h-[80vh]',
    'rounded-(--radius-lg) border-(--border-subtle) bg-(--surface-primary)',
  ),
  header: cn(
    'flex shrink-0 items-center justify-between',
    'px-(--space-5) py-(--space-4) border-b border-(--border-subtle)',
  ),
  // Section-scale was too small for compact drawers — use page title token
  title: cn(
    'm-0 text-(--text-primary)',
    'font-[family-name:var(--font-family)]',
    'font-[weight:var(--font-semibold)]',
    'text-[length:var(--text-app-title)]',
    'leading-[var(--leading-tight)]',
    'tracking-[var(--tracking-app-title)]',
  ),
  body: cn('min-h-0 flex-1 overflow-y-auto px-(--space-5) py-(--space-5)'),
  footer: cn(
    'shrink-0 border-t border-(--border-subtle) px-(--space-5) py-(--space-4)',
    'flex justify-end',
  ),
};

const settingsChrome: ShellChrome = {
  desktopPanel: cn(
    'flex flex-col gap-0 p-0',
    'w-[min(var(--settings-modal-width),96vw)] max-w-none',
    'h-[min(var(--settings-modal-height),88vh)] max-h-[88vh]',
    'rounded-(--radius-lg) border-(--border-subtle) bg-(--surface-primary)',
  ),
  header: cn(
    'flex shrink-0 items-center justify-between',
    'px-(--space-5) py-(--space-4) border-b border-(--border-subtle)',
  ),
  title: cn(
    'm-0 text-(--text-primary)',
    'font-[family-name:var(--font-family)]',
    'font-[weight:var(--font-semibold)]',
    'text-[length:var(--text-settings-title)]',
    'leading-[var(--leading-tight)]',
    'tracking-[var(--tracking-app-title)]',
  ),
  // No padding: the settings body is a two-column grid that paints its own
  // navigation background edge to edge, and the scroll lives in the pane.
  body: cn('min-h-0 flex-1 overflow-hidden p-0'),
  footer: cn(
    'shrink-0 border-t border-(--border-subtle) px-(--space-5) py-(--space-4)',
    'flex justify-end',
  ),
};

/**
 * Resolves chrome class sets for the requested density.
 *
 * @param density - Shell density mode.
 * @returns Class strings for panel regions.
 */
function chromeFor(density: AdaptiveFormShellDensity): ShellChrome {
  if (density === 'settings') return settingsChrome;
  return density === 'compact' ? compactChrome : defaultChrome;
}

type ShellBodyProps = {
  titleNode: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  chrome: ShellChrome;
  onClose: () => void;
};

/**
 * Renders the shared shell body: pinned header, scrollable content, optional footer.
 *
 * @param props - Body configuration including density chrome.
 * @returns The rendered shell body.
 */
function ShellBody({ titleNode, children, footer, chrome, onClose }: ShellBodyProps) {
  const { t } = useTranslation('common');
  return (
    <>
      <div className={chrome.header}>
        {titleNode}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('buttons.close')}
          className={cn(
            'flex items-center justify-center rounded-(--radius-md)',
            'h-8 w-8 text-(--text-secondary)',
            'border border-transparent bg-transparent',
            'transition-[var(--transition-standard)]',
            'hover:bg-(--surface-secondary) hover:border-(--border-subtle) hover:text-(--text-primary)',
          )}
        >
          <Cross2Icon width={16} height={16} />
        </button>
      </div>
      <div className={chrome.body}>{children}</div>
      {footer && <div className={chrome.footer}>{footer}</div>}
    </>
  );
}

/**
 * Adaptive form container: centered modal on desktop, full-height sheet on mobile.
 *
 * @param props - Shell configuration.
 * @returns The rendered shell (nothing when closed).
 */
export function AdaptiveFormShell({
  open,
  onClose,
  title,
  children,
  footer,
  density = 'default',
}: AdaptiveFormShellProps) {
  const isMobile = useIsMobile();
  const chrome = chromeFor(density);
  const onOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          aria-label={title}
          data-testid="adaptive-form-shell-mobile"
          data-density={density}
          className={mobilePanelClasses}
          hideClose
          onPointerDownOutside={preventSelectPortalDismiss}
          onInteractOutside={preventSelectPortalDismiss}
          onFocusOutside={preventSelectPortalDismiss}
          onEscapeKeyDown={preventOpenComboboxEscape}
        >
          <ShellBody
            titleNode={<SheetTitle className={chrome.title}>{title}</SheetTitle>}
            footer={footer}
            chrome={chrome}
            onClose={onClose}
          >
            {children}
          </ShellBody>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-label={title}
        data-testid="adaptive-form-shell-desktop"
        data-density={density}
        className={chrome.desktopPanel}
        onPointerDownOutside={preventSelectPortalDismiss}
        onInteractOutside={preventSelectPortalDismiss}
        onFocusOutside={preventSelectPortalDismiss}
        onEscapeKeyDown={preventOpenComboboxEscape}
      >
        <ShellBody
          titleNode={<DialogTitle className={chrome.title}>{title}</DialogTitle>}
          footer={footer}
          chrome={chrome}
          onClose={onClose}
        >
          {children}
        </ShellBody>
      </DialogContent>
    </Dialog>
  );
}
